import { HttpClient } from "@angular/common/http";
import { Inject, Injectable, inject } from "@angular/core";
import { EnvConfig, Environment } from "@doc/core";
import { map, of } from "rxjs";

import { ApiUrls } from "../backoffice-urls";

export interface DepartmentQuotaSummary {
  departmentId: number;
  quotaTotalBytes: number;
  quotaAllocatedBytes: number;
  quotaUsedBytes: number;
}

export interface DepartmentStorageItem {
  departmentId: number;
  departmentNameEn?: string;
  quotaTotalBytes?: number;
  totalStorageUsedBytes: number;
  personalStorageUsedBytes: number;
  departmentStorageUsedBytes: number;
}

export interface DepartmentStorageOverview {
  totalStorageUsedBytes: number;
  personalStorageUsedBytes: number;
  departmentStorageUsedBytes: number;
  departments: DepartmentStorageItem[];
}

@Injectable({
  providedIn: "root",
})
export class DepartmentService {
  private http = inject(HttpClient);
  private apiUrl: string;
  private baseUrl: string;

  constructor(@Inject(EnvConfig) private config: Environment) {
    this.baseUrl = this.config.mockUrl;
    this.apiUrl = this.config.apiUrl;
  }

  getDepartmentList(body: any) {
    const link = this.apiUrl + ApiUrls.getDepartmentList;
    return this.http.post<any>(link, body);
  }
  getWorkflowBydepartment(body: any) {
    const link = this.apiUrl + ApiUrls.getWorkflowByDepartment;
    return this.http.get<any>(link + "/" + body);
  }
  getUserbyDepartment(body: any) {
    const link = this.apiUrl + ApiUrls.getusersbydept;
    return this.http.post<any>(link, body);
  }

  exportDepartmentUserDetails(body: { DepartmentIds: number[] }) {
    const link = this.apiUrl + ApiUrls.exportDepartmentUserDetails;
    return this.http.post<any>(link, body);
  }

  getDepartmentUserStorageStats(body: { DepartmentIds: number[] }) {
    const link = this.apiUrl + ApiUrls.getDepartmentUserStorageStats;
    return this.http.post<any>(link, body);
  }

  getDepartmentStorageOverview(body: { DepartmentIds?: number[] } = {}) {
    const link = this.apiUrl + ApiUrls.getDepartmentStorageOverview;
    return this.http.post<any>(link, body);
  }

  getUserByRoleAndDepartment(body: any) {
    const link = this.apiUrl + ApiUrls.getUserByRoleAndDepartment;
    return this.http.post<any>(link, body);
  }

  getDepartmentByLoggedInUser() {
    const link = this.apiUrl + ApiUrls.getDepartmentByLoggedInUser;
    return this.http.get<any>(link);
  }

  createDepartment(body: any) {
    const link = this.apiUrl + ApiUrls.createDepartment;
    return this.http.post<any>(link, body);
  }

  getDepartmentById(id: number) {
    const link = this.apiUrl + ApiUrls.getDepartmentById + "/" + id;
    return this.http.get<any>(link);
  }

  getDepartmentQuotaSnapshot(id: number) {
    return this.getDepartmentById(id).pipe(
      map((response: any) => {
        const data = response?.data;
        if (!data) return null;
        return {
          departmentId: Number(data.departmentId ?? id),
          quotaTotalBytes: Number(data.quotaTotalBytes ?? 0),
          quotaAllocatedBytes: Number(data.quotaAllocatedBytes ?? 0),
          quotaUsedBytes: Number(data.quotaUsedBytes ?? 0),
        } as DepartmentQuotaSummary;
      })
    );
  }

  updateDepartment(body: any) {
    const link = this.apiUrl + ApiUrls.updateDepartment;
    return this.http.post<any>(link, body);
  }

  deleteDepartment(id: number) {
    const link = `${this.apiUrl}${ApiUrls.deleteDepartment}/${id}`;
    return this.http.post<any>(link, { id });
  }

  getDepartmentFolderHierarchyConfigByDept(deptId: number) {
    const link = `${this.apiUrl}${ApiUrls.getDepartmentFolderHierarchyConfigByDept}/${deptId}`;
    return this.http.get<any>(link);
  }

  saveDepartmentFolderHierarchyConfig(body: {
    deptId: number;
    isActive?: boolean;
    levels: { level: number; sortCriterion: string; order: 'ASC' | 'DESC' }[];
  }) {
    const link = this.apiUrl + ApiUrls.saveDepartmentFolderHierarchyConfig;
    return this.http.post<any>(link, body);
  }

  getCategoryList(body: any) {
    const link = this.apiUrl + ApiUrls.categoryGetList;
    return this.http.post<any>(link, body);
  }
  
  createCategory(body: any) {
    const link = this.apiUrl + ApiUrls.createCategory;
    return this.http.post<any>(link, body);
  }

  getCategoryById(id: number) {
    const link = this.apiUrl + ApiUrls.categoryById + "/" + id;
    return this.http.get<any>(link);
  }
   updateCategory(body: any) {
    const link = this.apiUrl + ApiUrls.updateCategory + "/" + body.categoryId;
    return this.http.post<any>(link, body);
  }

  deleteCategory(id: number) {
    const link = `${this.apiUrl}${ApiUrls.deleteCategory}/${id}`;
    return this.http.post<any>(link, { id });
  }
}
